import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';
import { useBrands, Brand } from '../../hooks/useBrands';

interface BrandSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBrandSelect: (brandId: string) => void;
}

export const BrandSelectorModal: React.FC<BrandSelectorModalProps> = ({
  isOpen,
  onClose,
  onBrandSelect
}) => {
  const { t } = useTranslation();
  const { data: brandsData, isLoading, isError } = useBrands();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  // Extract brands from the response
  const brands = brandsData ? (brandsData as any).brands || [] : [];

  const handleImageError = (brandId: string) => {
    setImageErrors(prev => ({
      ...prev,
      [brandId]: true
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-2xl w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              {t('personas.selectBrand', 'Select a Brand')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              <span className="ml-2 text-gray-600">{t('common.loading')}</span>
            </div>
          ) : isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="text-red-800 font-medium">{t('common.error')}</h3>
              <p className="text-red-600 text-sm mt-1">
                {t('personas.errors.brandsFetchFailed', 'Failed to fetch brands. Please try again.')}
              </p>
            </div>
          ) : brands.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {t('personas.noBrands', 'No brands available. Please create a brand first.')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto p-1">
              {brands.map((brand: Brand) => (
                <button
                  key={brand.id}
                  onClick={() => onBrandSelect(brand.id)}
                  className="flex flex-col items-center justify-center gap-3 p-4 rounded-lg border border-black hover:border-primary-600 hover:bg-primary-50 transition-colors h-full"
                >
                  {brand.logo && !imageErrors[brand.id] ? (
                    <img 
                      src={brand.logo} 
                      alt={brand.name}
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={() => handleImageError(brand.id)}
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400 text-xl font-bold">{brand.name.charAt(0)}</span>
                    </div>
                  )}
                  <div className="text-center">
                    <h3 className="font-medium text-gray-900">{brand.name}</h3>
                  </div>
                </button>
              ))}
            </div>
          )}

          {brands.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-500">
              {t('personas.brandSelectorHelp', 'Select a brand to create a persona for')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 